/**
 * @voltxampere/engine — Digital logic gate DC stamp (Sprint 41)
 *
 * MVP DC-only stamp that lands the Sprint 40 carry-over of 12 logic
 * IC variants. Per ADR-0061, the gate's output is treated as an
 * augmented-row voltage source whose value is recomputed each Newton
 * iteration from the threshold-applied input voltages read from the
 * previous iterate (`xPrev`). The Sprint 4 voltage-source pattern is
 * reused verbatim for the matrix stamping; only the RHS value is
 * dynamic.
 *
 * Truth-table registry:
 *
 *   NAND(a, b) = !(a && b)
 *   NOR (a, b) = !(a || b)
 *   AND (a, b) =   a && b
 *   OR  (a, b) =   a || b
 *   XOR (a, b) =   a !== b
 *   NOT (a)    =  !a
 *   D-FLOP     =  placeholder, returns vLow (Sprint 42+ adds full
 *                 sequential clock-edge tracking via transient state)
 *
 * Newton convergence caveat (ADR-0061 §"Newton convergence"): the
 * discrete `v_in >= threshold` comparison creates a step function in
 * the iteration map. If a gate's input voltage sits exactly on the
 * threshold the gate may flip HIGH ↔ LOW between iterations and the
 * solver can fail to converge. Sentinels (`digital-nand-2input`,
 * `digital-xor-2input`, `digital-not-1input`) only test clear-cut
 * voltages (0 V or 5 V) to avoid this borderline case.
 *
 * AC + transient stamps are intentionally not registered for
 * `digital-gate`. AC small-signal modelling around a non-smooth
 * threshold is undefined; transient propagation delay needs the per-
 * step state machine that Sprint 42+ ships. Both raise the engine's
 * standard "no stamp registered" error if an AC sweep or transient
 * solver encounters a `digital-gate` component, which is the desired
 * behaviour until those features land.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { defineDCStamp } from "./definition.js";
import { getTerminalNetId, getNumericParam } from "./terminal-utils.js";
/**
 * Truth-table registry. Adding a new combinational gate (e.g. a 3-
 * input NAND) is a one-line addition here plus a parameter-schema
 * enum update in `@voltxampere/components`. No engine math change.
 */
const GATE_TABLE = {
    NAND: { arity: 2, evaluate: ([a, b]) => !((a ?? false) && (b ?? false)) },
    NOR: { arity: 2, evaluate: ([a, b]) => !((a ?? false) || (b ?? false)) },
    AND: { arity: 2, evaluate: ([a, b]) => (a ?? false) && (b ?? false) },
    OR: { arity: 2, evaluate: ([a, b]) => (a ?? false) || (b ?? false) },
    XOR: { arity: 2, evaluate: ([a, b]) => (a ?? false) !== (b ?? false) },
    NOT: { arity: 1, evaluate: ([a]) => !(a ?? false) },
    // D-FLOP placeholder. The component drag-drops and saves into the
    // schema correctly, but DC simulation reads the output as `vLow`
    // until Sprint 42+ ships the sequential state machine. Inspector
    // surfaces the placeholder warning at the UI layer.
    "D-FLOP": { arity: 1, evaluate: () => false },
};
/** Read an MNA node voltage from the previous Newton iterate. */
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0; // ground rows are not in the matrix
    return xPrev[idx] ?? 0;
}
// Cast helper: the runtime parameter may be any string (the schema
// validator caps it to the GateFunction enum, but stamp callers
// receive a `Record<string, unknown>` and can supply anything). The
// wider `Record<string, GateSpec | undefined>` view lets the lookup
// produce `undefined` for unknown keys, which the stamp catches and
// reports as a contract violation.
const GATE_LOOKUP = GATE_TABLE;
export const digitalGateDCStamp = (ctx, component) => {
    const params = component.params;
    const gateFnRaw = params.gateFunction;
    const gateFn = typeof gateFnRaw === "string" ? gateFnRaw : "NAND";
    const spec = GATE_LOOKUP[gateFn];
    if (spec === undefined) {
        throw new ContractViolationError(`digitalGateDCStamp: component "${component.id}" has unknown gateFunction "${String(gateFnRaw)}"`);
    }
    const vHigh = getNumericParam(component, "vHigh", 5);
    const vLow = getNumericParam(component, "vLow", 0);
    const threshold = getNumericParam(component, "threshold", 2.5);
    // Sprint 43 (ADR-0064) — Schmitt-trigger hysteresis branch. Gated
    // behind `outputType: "schmitt"`; default `"standard"` short-
    // circuits to the Sprint 41 combinational path below so existing
    // sentinels stay bit-identical.
    const outputTypeRaw = params.outputType;
    const outputType = typeof outputTypeRaw === "string" ? outputTypeRaw : "standard";
    if (outputType === "schmitt") {
        const vTplus = getNumericParam(component, "vThresholdPlus", 1.7);
        const vTminus = getNumericParam(component, "vThresholdMinus", 0.9);
        // Schmitt is single-input by construction (74HC14 / 74HC132 /
        // 4584); we read `in1` regardless of the parametrised arity. The
        // 2-input Schmitt NAND (74HC132) is modelled by chaining a
        // standard NAND gate before a Schmitt NOT in user circuits — the
        // engine does not currently fuse the two into one stamp.
        const inIdx = ctx.nodeIndex(getTerminalNetId(component, "in1"));
        const vIn = nodeVoltage(ctx.xPrev, inIdx);
        const out = ctx.nodeIndex(getTerminalNetId(component, "out"));
        const vOutPrev = nodeVoltage(ctx.xPrev, out);
        const isHighNow = vOutPrev >= (vHigh + vLow) / 2;
        // Inverting Schmitt (74HC14 NOT semantics):
        //   currently HIGH → flip LOW only when vIn rises above V_T+
        //   currently LOW  → flip HIGH only when vIn falls below V_T-
        const effectiveThreshold = isHighNow ? vTplus : vTminus;
        const inputBoolean = vIn >= effectiveThreshold;
        const outputBoolean = !inputBoolean;
        const outputVoltage = outputBoolean ? vHigh : vLow;
        const k = ctx.vSourceIndex(component.id);
        ctx.addY(out, k, 1);
        ctx.addY(k, out, 1);
        ctx.rhs[k] = (ctx.rhs[k] ?? 0) + outputVoltage * ctx.sourceScale;
        return;
    }
    // Read input voltages from xPrev and threshold them to booleans.
    // Arity-1 gates (NOT, D-FLOP) only consult `in1`; the second pin
    // is reserved for symmetry but ignored here.
    const inputs = [];
    for (let i = 0; i < spec.arity; i += 1) {
        const pinId = `in${(i + 1).toString()}`;
        const idx = ctx.nodeIndex(getTerminalNetId(component, pinId));
        const v = nodeVoltage(ctx.xPrev, idx);
        inputs.push(v >= threshold);
    }
    const outputBoolean = spec.evaluate(inputs);
    const outputVoltage = outputBoolean ? vHigh : vLow;
    // Stamp the output node as an augmented-row voltage source. The
    // pattern mirrors `voltageSourceDCStamp` in stamps/linear/voltage-
    // source.ts: B[out, k] = +1, C[k, out] = +1, rhs[k] = V *
    // sourceScale. There is no `neg` terminal — the gate's output is
    // always referenced to the implicit MNA ground.
    const out = ctx.nodeIndex(getTerminalNetId(component, "out"));
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(out, k, 1);
    ctx.addY(k, out, 1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + outputVoltage * ctx.sourceScale;
};
/**
 * Digital gate carries one augmented MNA row for the output voltage
 * source — same `["vsource"]` augmentation contract as the canonical
 * voltage-source stamp. The planner allocates the row through the
 * `vSourceIndex` lookup the stamp uses above.
 */
export const digitalGateDCStampDefinition = defineDCStamp("digital-gate", digitalGateDCStamp, [
    "vsource",
]);
//# sourceMappingURL=digital-gate.js.map